// pages/LessonsPage.tsx
import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCourseStore } from '../store/courseStore';
import { useAuthStore } from '../store/authStore';
import { useProgressStore } from '../store/progressStore';
import BackButton from '../components/ui/BackButton';
import LessonCard from '../components/ui/LessonCard';

// Extract YouTube video ID from URL
function extractVideoId(url: string): string | null {
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) return url;
  const patterns = [
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

export default function LessonsPage() {
  const { topicId, subjectId, chapterId } = useParams();
  const navigate = useNavigate();
  const courses = useCourseStore((s) => s.courses);
  const fetchCourses = useCourseStore((s) => s.fetchCourses);
  const setLastPlayedLesson = useCourseStore((s) => s.setLastPlayedLesson);
  const user = useAuthStore((s) => s.user);
  const getVideoStatus = useProgressStore((s) => s.getVideoStatus);
  const getRecommendedLessonIndex = useProgressStore((s) => s.getRecommendedLessonIndex);

  useEffect(() => {
    if (courses.length === 0) fetchCourses();
  }, [courses.length, fetchCourses]);

  // Find chapter
  let chapter: import('../types/course').Chapter | undefined;
  let subjectName = '';
  for (const course of courses) {
    for (const topic of course.topics || []) {
      for (const subject of topic.subjects || []) {
        const ch = subject.chapters?.find(c => c.id === chapterId);
        if (ch) {
          chapter = ch;
          subjectName = subject.name;
          break;
        }
      }
      if (chapter) break;
    }
    if (chapter) break;
  }

  const videos = chapter?.videos || [];
  const videoIds = videos.map(v => v.id);
  const recommendedIndex = getRecommendedLessonIndex(videoIds);

  const handleLessonClick = (video: import('../types/course').Video) => {
    const ytId = extractVideoId(video.url);
    if (!ytId) return;

    // Save last played
    setLastPlayedLesson({
      videoId: ytId,
      videoTitle: video.title,
      chapterId: chapterId || '',
      chapterName: chapter?.name || '',
      subjectId: subjectId || '',
      subjectName,
    });

    // Save to progress store
    if (user) {
      useProgressStore.getState().setLastWatched({
        videoId: ytId,
        subjectId: subjectId || '',
        chapterName: chapter?.name || '',
        lessonTitle: video.title,
      });
    }

    navigate(`/watch/${ytId}?mode=course&chapterId=${chapterId}`);
  };

  return (
    <div className="min-h-screen bg-surface-50 safe-top safe-bottom page-enter">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-surface-50/80 backdrop-blur-lg px-4 pt-3 pb-2">
        <BackButton
          label="Chapters"
          to={`/course/${topicId}/${subjectId}`}
        />
      </div>

      <div className="px-5 pb-8 max-w-lg mx-auto">
        <h1 className="text-2xl font-extrabold text-surface-900 mb-1 animate-fade-up">
          {chapter?.name || 'Lessons'}
        </h1>
        {chapter?.description && (
          <p className="text-sm text-surface-300 mb-5 animate-fade-up">
            {chapter.description}
          </p>
        )}

        {videos.length === 0 ? (
          <div className="text-center py-20 text-surface-300">
            <span className="text-4xl mb-3 block">🎬</span>
            <p className="font-medium">No lessons available yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {videos.map((video, i) => {
              const status = user ? getVideoStatus(video.id) : 'not_started';
              return (
                <LessonCard
                  key={video.id}
                  video={video}
                  status={status}
                  isRecommended={i === recommendedIndex}
                  onClick={() => handleLessonClick(video)}
                  index={i}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
